"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaList,
  FaCog,
  FaBuilding,
  FaFileInvoiceDollar,
  FaMapMarkerAlt,
  FaUser,
  FaExchangeAlt,
  FaArrowsAltH,
} from "react-icons/fa";
import AssetRegistration from "./AssetRegistration";
import AssetList from "./AssetList";
import CategoryManagement from "./CategoryManagement";
import ServicesManagement from "./ServicesManagement";
import LocationManagement from "./LocationManagement";
import FixedAssetsInvoice from "./FixedAssetsInvoice";
import TradingAssetsInvoice from "./TradingAssetsInvoice";
import ServicesInvoice from "./ServicesInvoice";
import CustomerManagement from "./CustomerManagement";
import InternalTransfers from "./InternalTransfers";
import ExternalTransactions from "./ExternalTransactions";
import { useBusinessAuth } from "../context/BusinessAuthContext";

const AssetDashboard = () => {
  const [activeTab, setActiveTab] = useState("register");
  const { businessUser, logoutBusiness } = useBusinessAuth();
  const navigate = useNavigate();

  // businessRefId
  // business_type
  // external_business_id

  const renderContent = () => {
    switch (activeTab) {
      case "register":
        return <AssetRegistration />;
      case "list":
        return <AssetList />;
      case "customers":
        return <CustomerManagement />;
      case "categories":
        return <CategoryManagement />;
      case "services":
        return <ServicesManagement />;
      case "locations":
        return <LocationManagement />;
      case "internal-transfers":
        return <InternalTransfers />;
      case "external-transactions":
        return <ExternalTransactions />;
      case "fixed-assets-invoice":
        return <FixedAssetsInvoice />;
      case "trading-assets-invoice":
        return <TradingAssetsInvoice />;
      case "services-invoice":
        return <ServicesInvoice />;
      default:
        return <AssetRegistration />;
    }
  };

  const handleLogout = () => {
    const type = businessUser?.business_type;
    logoutBusiness();
    if (type === "arena") {
      navigate("/arena-dashboard");
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-slate-200/60 sticky top-0 z-40">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-6 w-px bg-slate-200"></div>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                  <FaBuilding className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                    Asset Management System
                  </h1>
                  <p className="text-sm text-slate-500">
                    Business Type: {businessUser?.business_type || "N/A"}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="flex min-h-[calc(100vh-80px)]">
        {/* Sidebar */}
        <nav className="w-72 bg-white/60 backdrop-blur-sm shadow-xl border-r border-slate-200/60">
          <div className="px-6 pt-6  border-b border-slate-200/60">
            <h3 className="text-lg font-bold text-slate-800">
              Asset Operations
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Manage your business assets
            </p>
          </div>

          <div className="p-6">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
              Main Functions
            </h4>
            <div className="space-y-2">
              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "register"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("register")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "register" ? "bg-white/20" : "bg-blue-100"
                  }`}
                >
                  <FaPlus
                    className={`w-4 h-4 ${
                      activeTab === "register" ? "text-white" : "text-blue-600"
                    }`}
                  />
                </div>
                <span>Register Asset</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "list"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("list")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "list" ? "bg-white/20" : "bg-emerald-100"
                  }`}
                >
                  <FaList
                    className={`w-4 h-4 ${
                      activeTab === "list" ? "text-white" : "text-emerald-600"
                    }`}
                  />
                </div>
                <span>View Assets</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "customers"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("customers")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "customers" ? "bg-white/20" : "bg-purple-100"
                  }`}
                >
                  <FaUser
                    className={`w-4 h-4 ${
                      activeTab === "customers"
                        ? "text-white"
                        : "text-purple-600"
                    }`}
                  />
                </div>
                <span>Manage Customers</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "categories"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("categories")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "categories" ? "bg-white/20" : "bg-purple-100"
                  }`}
                >
                  <FaCog
                    className={`w-4 h-4 ${
                      activeTab === "categories"
                        ? "text-white"
                        : "text-purple-600"
                    }`}
                  />
                </div>
                <span>Manage Categories</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "services"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("services")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "services" ? "bg-white/20" : "bg-green-100"
                  }`}
                >
                  <FaBuilding
                    className={`w-4 h-4 ${
                      activeTab === "services" ? "text-white" : "text-green-600"
                    }`}
                  />
                </div>
                <span>Manage Services and Extra Charges</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "locations"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("locations")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "locations" ? "bg-white/20" : "bg-amber-100"
                  }`}
                >
                  <FaMapMarkerAlt
                    className={`w-4 h-4 ${
                      activeTab === "locations"
                        ? "text-white"
                        : "text-amber-600"
                    }`}
                  />
                </div>
                <span>Manage Locations</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "internal-transfers"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("internal-transfers")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "internal-transfers"
                      ? "bg-white/20"
                      : "bg-teal-100"
                  }`}
                >
                  <FaExchangeAlt
                    className={`w-4 h-4 ${
                      activeTab === "internal-transfers"
                        ? "text-white"
                        : "text-teal-600"
                    }`}
                  />
                </div>
                <span>Internal Transfers</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "external-transactions"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("external-transactions")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "external-transactions"
                      ? "bg-white/20"
                      : "bg-cyan-100"
                  }`}
                >
                  <FaArrowsAltH
                    className={`w-4 h-4 ${
                      activeTab === "external-transactions"
                        ? "text-white"
                        : "text-cyan-600"
                    }`}
                  />
                </div>
                <span>External Transactions</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "fixed-assets-invoice"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("fixed-assets-invoice")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "fixed-assets-invoice"
                      ? "bg-white/20"
                      : "bg-blue-100"
                  }`}
                >
                  <FaFileInvoiceDollar
                    className={`w-4 h-4 ${
                      activeTab === "fixed-assets-invoice"
                        ? "text-white"
                        : "text-blue-600"
                    }`}
                  />
                </div>
                <span>Fixed Assets Invoice</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "trading-assets-invoice"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("trading-assets-invoice")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "trading-assets-invoice"
                      ? "bg-white/20"
                      : "bg-green-100"
                  }`}
                >
                  <FaFileInvoiceDollar
                    className={`w-4 h-4 ${
                      activeTab === "trading-assets-invoice"
                        ? "text-white"
                        : "text-green-600"
                    }`}
                  />
                </div>
                <span>Trading Assets Invoice</span>
              </button>

              <button
                className={`w-full flex items-center space-x-3 px-4 py-3.5 rounded-xl text-left transition-all duration-200 font-medium ${
                  activeTab === "services-invoice"
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-slate-700 hover:bg-slate-100/80 hover:text-slate-900"
                }`}
                onClick={() => setActiveTab("services-invoice")}
              >
                <div
                  className={`p-2 rounded-lg ${
                    activeTab === "services-invoice"
                      ? "bg-white/20"
                      : "bg-purple-100"
                  }`}
                >
                  <FaFileInvoiceDollar
                    className={`w-4 h-4 ${
                      activeTab === "services-invoice"
                        ? "text-white"
                        : "text-purple-600"
                    }`}
                  />
                </div>
                <span>Services Invoice</span>
              </button>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-8 overflow-auto">
          <div className="max-w-7xl mx-auto">{renderContent()}</div>
        </main>
      </div>
    </div>
  );
};

export default AssetDashboard;
